import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, Maximize2Icon, XIcon } from 'lucide-react';
import { samples } from '../../data/kit';
import { Highlight, SectionHeading } from '../ui/SectionHeading';
const ease = [0.23, 1, 0.32, 1] as const;
export function SamplesCarousel() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [zoomed, setZoomed] = useState(false);
  const reduceMotion = useReducedMotion();
  const sample = samples[index];
  const go = (next: number) => {
    setDirection(next > index ? 1 : -1);
    setIndex((next + samples.length) % samples.length);
  };
  useEffect(() => {
    if (!zoomed) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setZoomed(false);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [zoomed]);
  return <section className="bg-petrol py-14 sm:py-24" aria-roledescription="carousel" aria-label="Sample pages" data-id="element-141" data-mp="k74apuqo6jig">
      <div className="mx-auto max-w-[1180px] px-5 sm:px-8" data-id="element-142" data-mp="p1s2c2gyzu19">
        <SectionHeading tone="dark" title={<>
              Take a look <Highlight tone="dark" data-id="element-144" data-mp="3shfminu36utw">inside</Highlight>
            </>} description="A selection of real pages from the kit — not the full content." data-id="element-143" data-mp="t07j3omgk668" />

        <div className="mx-auto mt-10 max-w-[960px] sm:mt-12" data-id="element-145" data-mp="5by2d143q8ly">
          <div className="relative overflow-hidden rounded-3xl bg-petrol-soft p-4 sm:p-8" data-id="element-146" data-mp="5dsp8kyxoaso">
            <AnimatePresence mode="wait" initial={false} custom={direction} data-id="element-147" data-mp="ac2nx5jv9tj1">
              <motion.div key={index} initial={reduceMotion ? false : {
              opacity: 0,
              x: 24 * direction
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={reduceMotion ? undefined : {
              opacity: 0,
              x: -24 * direction
            }} transition={{
              duration: 0.22,
              ease
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.2} onDragEnd={(_, info) => {
              if (info.offset.x < -60) go(index + 1);else if (info.offset.x > 60) go(index - 1);
            }} className="grid items-center gap-5 md:grid-cols-[minmax(0,300px)_1fr] md:gap-10" role="group" aria-roledescription="slide" aria-label={`${index + 1} of ${samples.length}`} data-id="element-148" data-mp="1gq1xiamfe2sl">
                <button type="button" onClick={() => setZoomed(true)} aria-label={`Open full page: ${sample.title}`} className="mx-auto block w-full max-w-[250px] overflow-hidden rounded-lg bg-white shadow-lift sm:max-w-[300px]" data-id="element-149" data-mp="34161zosaw6nd">
                  <img src={sample.image} alt={sample.alt} draggable={false} className="aspect-[9/16] h-auto w-full select-none object-contain" data-id="element-150" data-mp="qtwx6p3h4wbm" />
                </button>
                <div className="text-center md:text-left" data-id="element-151" data-mp="lvloihox0ruz">
                  <p className="font-display text-lg font-bold text-teal-light" data-id="element-152" data-mp="lw507gnz2k0k">
                    {String(index + 1).padStart(2, '0')} / {String(samples.length).padStart(2, '0')}
                  </p>
                  <h3 className="mt-1 font-display text-[30px] font-extrabold uppercase leading-[.95] text-white sm:mt-2 sm:text-[42px]" data-id="element-153" data-mp="1eig7fa633j6c">
                    {sample.title}
                  </h3>
                  <p className="mt-3 text-base leading-relaxed text-white/75 sm:mt-4 sm:text-[17px]" data-id="element-154" data-mp="15lyvi4pwbh9j">{sample.body}</p>
                  <button type="button" onClick={() => setZoomed(true)} className="mt-5 inline-flex items-center gap-2 rounded-lg border border-white/25 px-4 py-2.5 text-sm font-bold text-white transition-colors duration-150 ease-out hover:bg-white/10" data-id="element-155" data-mp="3ldcvttpd04do">
                    <Maximize2Icon className="h-4 w-4" aria-hidden="true" data-id="element-156" data-mp="3vt5gl09xrebo" />
                    Read the full page
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="mt-6 flex items-center justify-center gap-5" data-id="element-157" data-mp="scomx66h79x7">
            <button type="button" onClick={() => go(index - 1)} aria-label="Previous sample" className="grid h-11 w-11 place-items-center rounded-full border border-white/25 text-white transition-colors duration-150 ease-out hover:bg-white/10" data-id="element-158" data-mp="nf6jtwf53ox4">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-159" data-mp="343iue3seffl0" />
            </button>
            <div className="flex gap-2" data-id="element-160" data-mp="2z2v1tdoiuqoo">
              {samples.map((s, i) => <button key={s.title} type="button" onClick={() => go(i)} aria-label={`Go to sample ${i + 1}`} aria-current={i === index} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out ${i === index ? 'w-7 bg-teal-light' : 'w-2.5 bg-white/30 hover:bg-white/50'}`} data-id="element-161" data-mp="1emev0b08fkfg" data-mpi={i} />)}
            </div>
            <button type="button" onClick={() => go(index + 1)} aria-label="Next sample" className="grid h-11 w-11 place-items-center rounded-full border border-white/25 text-white transition-colors duration-150 ease-out hover:bg-white/10" data-id="element-162" data-mp="22a53k9iqq7yw">
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-163" data-mp="cxl4thxup3ec" />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-164" data-mp="gzy0fdivvkxe">
        {zoomed && <motion.div role="dialog" aria-modal="true" aria-label={sample.title} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease
      }} className="fixed inset-0 z-50 overflow-y-auto bg-petrol-deep/95" onClick={() => setZoomed(false)} data-id="element-165" data-mp="1k7zexrol546a">
            <div className="sticky top-0 flex justify-end p-3" data-id="element-166" data-mp="236lfn5g6wt9o">
              <button type="button" onClick={() => setZoomed(false)} aria-label="Close" autoFocus className="grid h-11 w-11 place-items-center rounded-full bg-white text-petrol shadow-card" data-id="element-167" data-mp="1y8tncabmv54y">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-168" data-mp="280ppbwgubfeg" />
              </button>
            </div>
            <motion.img src={sample.image} alt={sample.alt} initial={reduceMotion ? false : {
          scale: 0.96,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          duration: 0.22,
          ease
        }} onClick={e => e.stopPropagation()} className="mx-auto mb-10 h-auto w-[calc(100%-24px)] max-w-[720px] rounded-lg bg-white" data-id="element-169" data-mp="2gz1ncjk1dfgx" />
          </motion.div>}
      </AnimatePresence>
    </section>;
}